<template>
  <div id="app" :class="{ 'app-shell--without-progress': !showGlobalProgressBar }">
    <ProgressBar v-if="showGlobalProgressBar" :progress="uiStore.progressPercent" />

    <!-- App shell for authenticated operational routes — renders its own scoped LoadingOverlay -->
    <AppShell v-if="route.meta.layout === 'app'" />

    <!-- Public shell for home, terms, entry, invite — renders its own global LoadingOverlay -->
    <template v-else>
      <LoadingOverlay />
      <HeaderComp
        :current-section="currentSection"
        @open-auth="openAuthModal"
        @show-how-it-works="openHowItWorksModal"
      />
      <div class="content">
        <ErrorBoundary>
          <router-view
            @mouseenter="isHovering = true"
            @mouseleave="isHovering = false"
            @show-how-it-works="openHowItWorksModal"
            @show-case-studies="openCaseStudiesModal"
          />
        </ErrorBoundary>
      </div>
    </template>

    <HowItWorksModal
      v-if="showHowItWorksModal"
      @close="closeHowItWorksModal"
      :current-page="currentSection"
    />
    <CaseStudiesModal
      v-if="showCaseStudiesModal"
      @close="closeCaseStudiesModal"
    />

    <!-- Auth modal flow — rendered at App.vue level so it works in both shells -->
    <AuthModal
      v-if="showAuthModal"
      @close="closeAuthModal"
      @authenticated="handleAuthenticated"
      @verification-required="handleVerificationRequired"
      @open-signup="handleOpenSignup"
    />
    <RegistrationModal
      v-if="showRegistrationModal"
      @close="closeRegistrationModal"
      @authenticated="handleSignupAuthenticated"
      @verification-required="handleSignupVerificationRequired"
    />
    <AuthVerificationModal
      v-if="showVerificationModal"
      :email="pendingEmail"
      :username="pendingEmail"
      :code="verificationCode"
      :is-submitting="isSubmittingVerification"
      :is-resending="isResendingVerification"
      :error="verificationError"
      @update:code="verificationCode = $event"
      @confirm="confirmVerification"
      @resend="resendVerification"
      @close="closeVerification"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, watch, computed } from 'vue'
import { useRoute } from 'vue-router'
import AppShell from './components/shell/AppShell.vue'
import ErrorBoundary from './components/ErrorBoundary.vue'
import HeaderComp from './components/HeaderComp.vue'
import HowItWorksModal from './components/HowItWorksModal.vue'
import CaseStudiesModal from './components/CaseStudiesModal.vue'
import ProgressBar from './components/ProgressBar.vue'
import LoadingOverlay from './components/LoadingOverlay.vue'
import AuthModal from './components/AuthModal.vue'
import RegistrationModal from './components/RegistrationModal.vue'
import AuthVerificationModal from './components/AuthVerificationModal.vue'
import { useUIStore } from './store/ui.store'
import { useAuthStore } from './store/auth.store'
import router from './router'

export default defineComponent({
  name: 'App',
  components: {
    AppShell,
    ErrorBoundary,
    HeaderComp,
    HowItWorksModal,
    CaseStudiesModal,
    ProgressBar,
    LoadingOverlay,
    AuthModal,
    RegistrationModal,
    AuthVerificationModal,
  },
  setup() {
    const uiStore = useUIStore()
    const authStore = useAuthStore()
    const route = useRoute()

    const currentSection = computed(() => uiStore.currentSection);
    const showGlobalProgressBar = computed(() => Boolean(route.meta.showProgressBar))

    // Modal State
    const showHowItWorksModal = ref(false)
    const showCaseStudiesModal = ref(false)
    const showAuthModal = ref(false)
    const showRegistrationModal = ref(false)
    const showVerificationModal = ref(false)
    // True while we're inside the top-right signup flow, so the post-verify
    // hand-off knows to refresh the user back to the home screen instead of
    // staying on whichever section they triggered signup from.
    const signupFlowActive = ref(false)

    watch(showHowItWorksModal, (isOpen) => {
      if (isOpen) {
        uiStore.openModal()
      } else {
        uiStore.closeModal()
      }
    })

    watch(showCaseStudiesModal, (isOpen) => {
      if (isOpen) {
        uiStore.openModal()
      } else {
        uiStore.closeModal()
      }
    })

    watch(showAuthModal, (isOpen) => {
      if (isOpen) {
        uiStore.openModal()
      } else {
        uiStore.closeModal()
      }
    })

    watch(showRegistrationModal, (isOpen) => {
      if (isOpen) {
        uiStore.openModal()
      } else {
        uiStore.closeModal()
      }
    })

    watch(showVerificationModal, (isOpen) => {
      if (isOpen) {
        uiStore.openModal()
      } else {
        uiStore.closeModal()
      }
    })

    const pendingEmail = ref('')
    const pendingPassword = ref('')
    const verificationCode = ref('')
    const verificationError = ref('')
    const isSubmittingVerification = ref(false)
    const isResendingVerification = ref(false)

    // Auth functions
    const openAuthModal = () => {
      showAuthModal.value = true
    }

    const closeAuthModal = () => {
      showAuthModal.value = false
    }

    const handleAuthenticated = async () => {
      showAuthModal.value = false
      await authStore.checkAuth()
    }

    const closeRegistrationModal = () => {
      showRegistrationModal.value = false
      signupFlowActive.value = false
    }

    const handleOpenSignup = () => {
      showAuthModal.value = false
      signupFlowActive.value = true
      showRegistrationModal.value = true
    }

    const goHomeAfterSignup = async () => {
      if (router.currentRoute.value.path !== '/') {
        await router.push('/')
      }
    }

    const handleSignupAuthenticated = async () => {
      showRegistrationModal.value = false
      await authStore.checkAuth()
      await goHomeAfterSignup()
      signupFlowActive.value = false
    }

    const handleSignupVerificationRequired = (payload?: { email?: string; password?: string }) => {
      showRegistrationModal.value = false
      pendingEmail.value = payload?.email || ''
      pendingPassword.value = payload?.password || ''
      verificationCode.value = ''
      verificationError.value = ''
      showVerificationModal.value = true
    }

    const handleVerificationRequired = (payload?: { email?: string; password?: string }) => {
      pendingEmail.value = payload?.email || ''
      pendingPassword.value = payload?.password || ''
      verificationCode.value = ''
      verificationError.value = ''
      showVerificationModal.value = true
    }

    const confirmVerification = async () => {
      if (!pendingEmail.value || !verificationCode.value) {
        verificationError.value = 'Enter the code from your email.'
        return
      }
      isSubmittingVerification.value = true
      verificationError.value = ''
      try {
        await authStore.confirmSignUp(pendingEmail.value, verificationCode.value)
        if (pendingPassword.value) {
          await authStore.login(pendingEmail.value, pendingPassword.value)
        }
        await authStore.checkAuth()
        showVerificationModal.value = false
        showAuthModal.value = false
        if (signupFlowActive.value) {
          await goHomeAfterSignup()
          signupFlowActive.value = false
        }
      } catch (e: unknown) {
        const error = e as Error
        verificationError.value = error?.message || 'Verification failed. Please try again.'
      } finally {
        isSubmittingVerification.value = false
      }
    }

    const resendVerification = async () => {
      if (!pendingEmail.value) return
      isResendingVerification.value = true
      verificationError.value = ''
      try {
        await authStore.resendVerification(pendingEmail.value)
      } catch (e: unknown) {
        const error = e as Error
        verificationError.value = error?.message || 'Could not resend code.'
      } finally {
        isResendingVerification.value = false
      }
    }

    const closeVerification = () => {
      showVerificationModal.value = false
    }

    const isHovering = ref(false)

    let teardownKeyboardDetection: (() => void) | null = null

    /**
     * Toggle `.keyboard-open` on <html> so CSS can hide the fixed section
     * footer while an input is focused.
     *
     * We combine TWO signals to avoid the class sticking, because viewport
     * height alone lied too often — iOS Safari's URL bar collapse / expand,
     * orientation changes, and momentum scrolling can all cross a naive
     * height threshold and leave the footer wrongly hidden after the
     * keyboard is gone.
     *
     *   1. `focusin`/`focusout` on an input-like element flips the class.
     *      This is the only authoritative signal that a keyboard intent
     *      exists — blur always means "keyboard is going away."
     *   2. A visualViewport height shrink >150px also flips it (covers the
     *      rare case a keyboard opens without a focusin event, e.g.
     *      programmatic focus or some PWA paths). When focus leaves,
     *      focusout wins and the class is always cleared.
     */
    const setupKeyboardDetection = () => {
      if (typeof window === 'undefined' || typeof document === 'undefined') return

      const isInputLike = (el: EventTarget | null): boolean => {
        if (!(el instanceof HTMLElement)) return false
        const tag = el.tagName
        return (
          tag === 'INPUT' ||
          tag === 'TEXTAREA' ||
          tag === 'SELECT' ||
          el.isContentEditable
        )
      }

      const setOpen = (open: boolean) => {
        document.documentElement.classList.toggle('keyboard-open', open)
      }

      const focusEvents = {
        in: (e: FocusEvent) => {
          if (isInputLike(e.target)) setOpen(true)
        },
        out: (e: FocusEvent) => {
          // Blur defers a tick so focus moving between inputs doesn't
          // flicker the class off-then-on. Also confirms via
          // `document.activeElement` which is authoritative after focusout.
          if (!isInputLike(e.target)) return
          setTimeout(() => {
            if (!isInputLike(document.activeElement)) setOpen(false)
          }, 40)
        },
      }

      document.addEventListener('focusin', focusEvents.in)
      document.addEventListener('focusout', focusEvents.out)

      const vv = window.visualViewport
      const THRESHOLD = 150
      const vvUpdate = () => {
        if (!vv) return
        const heightShrunk = window.innerHeight - vv.height > THRESHOLD
        // Only TURN ON via viewport — let focusout handle turning off so
        // transient URL-bar resizes can't leave the class stuck.
        if (heightShrunk && isInputLike(document.activeElement)) setOpen(true)
      }
      vv?.addEventListener('resize', vvUpdate)
      vvUpdate()

      teardownKeyboardDetection = () => {
        document.removeEventListener('focusin', focusEvents.in)
        document.removeEventListener('focusout', focusEvents.out)
        vv?.removeEventListener('resize', vvUpdate)
        setOpen(false)
      }
    }

    onMounted(() => {
      authStore.checkAuth()
      setupKeyboardDetection()
    })

    const openHowItWorksModal = () => {
      showHowItWorksModal.value = true
    }

    const closeHowItWorksModal = () => {
      showHowItWorksModal.value = false
    }

    const openCaseStudiesModal = () => {
      showCaseStudiesModal.value = true
    }

    const closeCaseStudiesModal = () => {
      showCaseStudiesModal.value = false
    }


    return {
      route,
      isHovering,
      showHowItWorksModal,
      openHowItWorksModal,
      closeHowItWorksModal,
      showCaseStudiesModal,
      openCaseStudiesModal,
      closeCaseStudiesModal,
      uiStore,
      authStore,
      showGlobalProgressBar,
      currentSection,
      showAuthModal,
      openAuthModal,
      closeAuthModal,
      handleAuthenticated,
      handleVerificationRequired,
      showRegistrationModal,
      closeRegistrationModal,
      handleOpenSignup,
      handleSignupAuthenticated,
      handleSignupVerificationRequired,
      showVerificationModal,
      pendingEmail,
      verificationCode,
      isSubmittingVerification,
      isResendingVerification,
      verificationError,
      confirmVerification,
      resendVerification,
      closeVerification,
    }
  }
})
</script>

<style>
#app {
  font-family: var(--font-sans);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  width: 100%;
  max-width: 100%;
  min-height: 100vh;
  margin: 0;
  position: relative;
  overflow-x: clip;
}

#app.app-shell--without-progress::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: var(--base-layout-fixed-progress-bar-height);
  background: var(--color-background);
  z-index: calc(var(--base-layout-fixed-progress-bar-z-index) - 1);
  pointer-events: none;
}

#app.app-shell--without-progress::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: var(--base-layout-fixed-progress-bar-height);
  background: var(--color-background);
  z-index: calc(var(--base-layout-fixed-progress-bar-z-index) - 1);
  pointer-events: none;
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.content {
  padding-top: 0;
}

body {
  margin: 0;
  padding: 0;
  overflow-x: hidden;
  min-height: 100vh;
  width: 100%;
  max-width: 100%;
  overscroll-behavior-x: none;
}

html {
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  overscroll-behavior-x: none;
}

@media (max-width: 768px) {
  #app:has(.app-header .nav-menu.menu-open) > .content,
  #app:has(.app-header .auth-menu) > .content {
    filter: blur(0.28rem) saturate(0.92);
    opacity: 0.74;
    transition: filter 0.18s ease, opacity 0.18s ease;
  }

  #app:has(.app-header .nav-menu.menu-open) > .app-header .header-right,
  #app:has(.app-header .auth-menu) > .app-header .header-left {
    filter: blur(0.28rem) saturate(0.92);
    opacity: 0.74;
    transition: filter 0.18s ease, opacity 0.18s ease;
  }

  #app:has(.app-header .nav-menu.menu-open) > .app-header .header-left,
  #app:has(.app-header .auth-menu) > .app-header .header-right {
    position: relative;
    z-index: calc(var(--base-layout-fixed-nav-menu-z-index) + 40);
  }
}
</style>
